import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bell,
  Check,
  Copy,
  ExternalLink,
  PackageCheck,
  RefreshCw,
  ShoppingCart,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  parsePlainTextDeck,
  STARTER_DECKS,
  type ImportedDeckPayload,
} from '#shared';
import {
  buildSimulatedStockCheckUpdates,
  deckPayloadToCartItems,
  DEFAULT_CART_PREFERENCES,
  optimizeDeckCart,
  reoptimizeAfterStockCheck,
  seedVendorQuotesForItems,
  VENDOR_SHIPPING_SCHEDULES,
  type CardCondition,
  type CartDeckItem,
  type CartOptimizationPreferences,
  type FinishPreference,
  type OptimizationMode,
  type OptimizedDeckCartResult,
  type PrintingPreference,
  type ReoptimizedCartOutcome,
  type StockVerificationUpdate,
  type VendorId,
  type VendorQuote,
} from '#cart-optimizer';
import {
  playtableApi,
  type AccountProfileDTO,
  type DeckPriceAlertDTO,
  type SavedDeckDTO,
} from '../lib/clientApi';

interface DeckCartModalProps {
  initialDeck?: ImportedDeckPayload | null;
  profile: AccountProfileDTO | null;
  ensureSignedIn?: () => Promise<AccountProfileDTO | null>;
  onClose: () => void;
}

const ALL_VENDORS: VendorId[] = [
  'tcgplayer_direct',
  'tcgplayer_market',
  'card_kingdom',
  'coolstuffinc',
  'starcitygames',
  'cardmarket',
];

export const DeckCartModal: React.FC<DeckCartModalProps> = ({
  initialDeck,
  profile,
  ensureSignedIn,
  onClose,
}) => {
  const [selectedDeck, setSelectedDeck] = useState<ImportedDeckPayload>(() => {
    return initialDeck ?? STARTER_DECKS[0];
  });
  const [savedDecks, setSavedDecks] = useState<SavedDeckDTO[]>([]);
  const [customPasteOpen, setCustomPasteOpen] = useState(false);
  const [customDeckText, setCustomDeckText] = useState('');

  const [ownedText, setOwnedText] = useState('');
  const [ownedPanelOpen, setOwnedPanelOpen] = useState(false);

  const [mode, setMode] = useState<OptimizationMode>(DEFAULT_CART_PREFERENCES.mode);
  const [minCondition, setMinCondition] = useState<CardCondition>(DEFAULT_CART_PREFERENCES.minCondition);
  const [printingPreference, setPrintingPreference] = useState<PrintingPreference>(
    DEFAULT_CART_PREFERENCES.printingPreference
  );
  const [finishPreference, setFinishPreference] = useState<FinishPreference>(
    DEFAULT_CART_PREFERENCES.finishPreference
  );
  const [excludeBasicLands, setExcludeBasicLands] = useState<boolean>(true);
  const [allowedVendors, setAllowedVendors] = useState<VendorId[]>(
    DEFAULT_CART_PREFERENCES.allowedVendors ?? ALL_VENDORS.slice(0, 5)
  );

  const [items, setItems] = useState<CartDeckItem[]>([]);
  const [quotes, setQuotes] = useState<VendorQuote[]>([]);
  const [result, setResult] = useState<OptimizedDeckCartResult | null>(null);
  const [reoptBanner, setReoptBanner] = useState<ReoptimizedCartOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiedSellerId, setCopiedSellerId] = useState<string | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});

  // Price-Drop Alerts state
  const [alerts, setAlerts] = useState<DeckPriceAlertDTO[]>([]);
  const [targetUsdInput, setTargetUsdInput] = useState<string>('45.00');
  const [alertFeedback, setAlertFeedback] = useState<string | null>(null);

  const prefs: CartOptimizationPreferences = useMemo(
    () => ({
      mode,
      minCondition,
      printingPreference,
      finishPreference,
      excludeBasicLands,
      allowedVendors: allowedVendors.length > 0 ? allowedVendors : ALL_VENDORS,
    }),
    [mode, minCondition, printingPreference, finishPreference, excludeBasicLands, allowedVendors]
  );

  useEffect(() => {
    if (profile) {
      playtableApi
        .listSavedDecks()
        .then((r) => setSavedDecks(r.decks))
        .catch(() => {});
      playtableApi
        .listPriceAlerts()
        .then((r) => setAlerts(r.alerts))
        .catch(() => {});
    }
  }, [profile]);

  const runOptimization = useCallback(
    async (deckToOptimize: ImportedDeckPayload, currentPrefs: CartOptimizationPreferences, currentOwned: string) => {
      setBusy(true);
      setReoptBanner(null);
      try {
        const res = await playtableApi.optimizeDeckCart({
          deck: deckToOptimize,
          ownedCollectionText: currentOwned,
          preferences: currentPrefs,
        });
        setItems(res.items);
        setQuotes(res.quotes);
        setResult(res.result);
        setTargetUsdInput(Math.max(1, Math.round(res.result.grandTotalUsd * 0.88 * 100) / 100).toFixed(2));
      } catch {
        // Offline / pure client fallback via standalone @playtable/cart-optimizer
        const localItems = deckPayloadToCartItems(deckToOptimize, true);
        const localQuotes = seedVendorQuotesForItems(localItems);
        const localRes = optimizeDeckCart({
          items: localItems,
          quotes: localQuotes,
          ownedCollectionText: currentOwned,
          preferences: currentPrefs,
        });
        setItems(localItems);
        setQuotes(localQuotes);
        setResult(localRes);
        setTargetUsdInput(Math.max(1, Math.round(localRes.grandTotalUsd * 0.88 * 100) / 100).toFixed(2));
      } finally {
        setBusy(false);
      }
    },
    []
  );

  useEffect(() => {
    runOptimization(selectedDeck, prefs, ownedText);
  }, [selectedDeck, prefs, ownedText, runOptimization]);

  const handleStockReoptimize = async (customUpdates?: StockVerificationUpdate[]) => {
    if (!result) return;
    setBusy(true);
    try {
      const outcome = await playtableApi.reoptimizeDeckCart({
        items,
        quotes,
        previousResult: result,
        stockUpdates: customUpdates,
        ownedCollectionText: ownedText,
        preferences: prefs,
      });
      setReoptBanner(outcome);
      setQuotes(outcome.updatedQuotes);
      setResult(outcome.updatedResult);
    } catch {
      const updates = customUpdates ?? buildSimulatedStockCheckUpdates(result);
      const localOutcome = reoptimizeAfterStockCheck({
        items,
        quotes,
        previousResult: result,
        stockUpdates: updates,
        ownedCollectionText: ownedText,
        preferences: prefs,
      });
      setReoptBanner(localOutcome);
      setQuotes(localOutcome.updatedQuotes);
      setResult(localOutcome.updatedResult);
    } finally {
      setBusy(false);
    }
  };

  const handleCreateAlert = async () => {
    try {
      if (!profile && ensureSignedIn) {
        const signed = await ensureSignedIn();
        if (!signed) return;
      }
      const targetNum = parseFloat(targetUsdInput);
      if (!Number.isFinite(targetNum) || targetNum <= 0) return;

      const created = await playtableApi.createPriceAlert({
        deckName: selectedDeck.name,
        targetTotalUsd: targetNum,
        deck: selectedDeck,
        ownedCollectionText: ownedText,
        preferences: prefs,
      });
      setAlerts((prev) => [created.alert, ...prev]);
      setAlertFeedback(
        created.alert.triggered
          ? `Target already met ($${created.alert.lastCheckedTotalUsd.toFixed(2)} <= $${created.alert.targetTotalUsd.toFixed(2)})!`
          : `Price-drop alert saved for <= $${created.alert.targetTotalUsd.toFixed(2)}!`
      );
      setTimeout(() => setAlertFeedback(null), 3500);
    } catch (err: any) {
      setAlertFeedback(err?.message ?? 'Sign in to save price-drop alerts.');
    }
  };

  const toggleVendor = (vid: VendorId) => {
    setAllowedVendors((prev) => {
      if (prev.includes(vid)) {
        return prev.length > 1 ? prev.filter((v) => v !== vid) : prev;
      }
      return [...prev, vid];
    });
  };

  return (
    <div
      className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Multi-Vendor Deck Cart & Shipping Optimizer"
    >
      <div className="w-full max-w-4xl rounded-2xl bg-slate-950 border border-emerald-500/35 shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-emerald-950/70 via-slate-950 to-slate-950 border-b border-slate-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
              <ShoppingCart className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <h2 className="font-extrabold text-sm sm:text-base text-white truncate">
                Buy Deck — Multi-Vendor Cart & Shipping Optimizer
              </h2>
              <p className="text-[11px] text-slate-400 truncate">
                Shipping-threshold consolidation across TCGplayer Direct/Market, Card Kingdom, CoolStuffInc, SCG & Cardmarket
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Cart Optimizer"
            className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3.5 overflow-y-auto text-xs">
          {/* Deck Selector & Collection Subtraction Bar */}
          <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[220px]">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Deck:</span>
                <select
                  aria-label="Select deck to optimize"
                  value={selectedDeck.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const fromSaved = savedDecks.find((s) => s.name === val)?.deck;
                    const fromStarter = STARTER_DECKS.find((s) => s.name === val);
                    if (fromSaved) setSelectedDeck(fromSaved);
                    else if (fromStarter) setSelectedDeck(fromStarter);
                  }}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 font-bold"
                >
                  {initialDeck &&
                    !STARTER_DECKS.some((s) => s.name === initialDeck.name) &&
                    !savedDecks.some((s) => s.name === initialDeck.name) && (
                      <option value={initialDeck.name}>{initialDeck.name} (Current Table)</option>
                    )}
                  <optgroup label="Starter Precons">
                    {STARTER_DECKS.map((sd) => (
                      <option key={sd.deckId} value={sd.name}>
                        {sd.name}
                      </option>
                    ))}
                  </optgroup>
                  {savedDecks.length > 0 && (
                    <optgroup label="My Saved Decks">
                      {savedDecks.map((sd) => (
                        <option key={sd.deckId} value={sd.name}>
                          {sd.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>

                <button
                  type="button"
                  onClick={() => setCustomPasteOpen((v) => !v)}
                  className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-semibold"
                >
                  {customPasteOpen ? 'Hide Paste' : 'Paste Custom List'}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeBasicLands}
                    onChange={(e) => setExcludeBasicLands(e.target.checked)}
                    className="accent-emerald-500"
                  />
                  <span className="text-[11px] text-slate-200 font-semibold">Exclude Basic Lands</span>
                </label>

                <button
                  type="button"
                  onClick={() => setOwnedPanelOpen((v) => !v)}
                  className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold ${
                    ownedText.trim()
                      ? 'bg-cyan-950/80 border-cyan-500/60 text-cyan-200'
                      : 'bg-slate-950 border-slate-700 text-slate-300 hover:text-white'
                  }`}
                >
                  Subtract Owned Collection
                  {result && result.collectionSummary.subtractedOwnedQty > 0
                    ? ` (-${result.collectionSummary.subtractedOwnedQty})`
                    : ''}
                </button>
              </div>
            </div>

            {customPasteOpen && (
              <div className="pt-2 border-t border-slate-800 space-y-2">
                <textarea
                  rows={4}
                  value={customDeckText}
                  onChange={(e) => setCustomDeckText(e.target.value)}
                  placeholder="Paste decklist (e.g. 1 Sol Ring, 1 Rhystic Study, 4 Lightning Bolt)..."
                  className="w-full p-2 rounded-lg bg-slate-950 border border-slate-700 font-mono text-[11px]"
                />
                <button
                  type="button"
                  disabled={!customDeckText.trim()}
                  onClick={() => {
                    const parsed = parsePlainTextDeck(customDeckText, 'Custom Cart List', 'commander');
                    setSelectedDeck(parsed.deck);
                    setCustomPasteOpen(false);
                  }}
                  className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-[11px]"
                >
                  Optimize Pasted List
                </button>
              </div>
            )}

            {ownedPanelOpen && (
              <div className="pt-2 border-t border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between text-[10px] text-cyan-300 font-bold uppercase">
                  <span>Paste Owned Collection (Plain Text or CSV Name,Quantity)</span>
                  {ownedText && (
                    <button
                      type="button"
                      onClick={() => setOwnedText('')}
                      className="text-rose-300 hover:underline"
                    >
                      Clear Owned
                    </button>
                  )}
                </div>
                <textarea
                  rows={3}
                  value={ownedText}
                  onChange={(e) => setOwnedText(e.target.value)}
                  placeholder={'1 Sol Ring\n1 Arcane Signet\n1 Command Tower'}
                  className="w-full p-2 rounded-lg bg-slate-950 border border-slate-700 font-mono text-[11px]"
                />
              </div>
            )}
          </div>

          {/* Solver Mode & Preferences Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {/* Solver Objective */}
            <div className="md:col-span-2 p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
              <div className="text-[10px] font-bold uppercase text-slate-400">Optimization Objective</div>
              <div className="grid grid-cols-3 gap-1">
                {(
                  [
                    ['cheapest_total', 'Cheapest Total'],
                    ['balanced', 'Balanced'],
                    ['fewest_packages', 'Fewest Packages'],
                  ] as const
                ).map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`py-1.5 px-2 rounded-lg font-bold text-[11px] border transition-colors ${
                      mode === m
                        ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                        : 'bg-slate-950 text-slate-300 border-slate-800 hover:border-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Condition & Finish */}
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
              <div className="text-[10px] font-bold uppercase text-slate-400">Min Condition & Foil</div>
              <div className="flex gap-1.5">
                <select
                  aria-label="Minimum Card Condition"
                  value={minCondition}
                  onChange={(e) => setMinCondition(e.target.value as CardCondition)}
                  className="flex-1 px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 font-bold"
                >
                  <option value="NM">NM Only</option>
                  <option value="LP">LP+</option>
                  <option value="MP">MP+</option>
                  <option value="HP">HP+ (Budget)</option>
                </select>
                <select
                  aria-label="Foil Preference"
                  value={finishPreference}
                  onChange={(e) => setFinishPreference(e.target.value as FinishPreference)}
                  className="flex-1 px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 font-bold"
                >
                  <option value="nonfoil">Nonfoil</option>
                  <option value="foil">Foil</option>
                  <option value="any">Any</option>
                </select>
              </div>
            </div>

            {/* Printing Preference */}
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
              <div className="text-[10px] font-bold uppercase text-slate-400">Set Printing</div>
              <select
                aria-label="Printing Preference"
                value={printingPreference}
                onChange={(e) => setPrintingPreference(e.target.value as PrintingPreference)}
                className="w-full px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 font-bold"
              >
                <option value="cheapest_printing">Cheapest Printing</option>
                <option value="exact_printing">Exact Deck Set Code</option>
              </select>
            </div>
          </div>

          {/* Vendor Filter Chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase text-slate-400 mr-1">Stores:</span>
            {ALL_VENDORS.map((vid) => {
              const active = allowedVendors.includes(vid);
              const sched = VENDOR_SHIPPING_SCHEDULES[vid];
              return (
                <button
                  key={vid}
                  type="button"
                  onClick={() => toggleVendor(vid)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                    active
                      ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-200'
                      : 'bg-slate-900 border-slate-800 text-slate-500'
                  }`}
                >
                  {sched.vendorLabel} ({sched.region})
                </button>
              );
            })}
          </div>

          {/* Summary & Savings Callout */}
          {result && (
            <div className="p-3.5 rounded-2xl bg-gradient-to-r from-emerald-950/60 via-slate-900 to-slate-900 border border-emerald-500/40 flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>
                    Optimized Grand Total ({result.collectionSummary.totalNeededQty} cards across{' '}
                    {result.packageCount} {result.packageCount === 1 ? 'package' : 'packages'})
                  </span>
                </div>
                <div className="flex items-baseline gap-2.5">
                  <span className="text-2xl font-black text-white">${result.grandTotalUsd.toFixed(2)}</span>
                  <span className="text-[11px] text-slate-300">
                    (${result.cardsSubtotalUsd.toFixed(2)} cards + ${result.shippingTotalUsd.toFixed(2)} shipping)
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  {result.savingsVsNaiveUsd > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold">
                      Saves ${result.savingsVsNaiveUsd.toFixed(2)} vs. naive per-card ({result.naivePackageCount}{' '}
                      shipments)
                    </span>
                  )}
                  {result.savingsVsSingleSellerUsd > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 font-bold">
                      Saves ${result.savingsVsSingleSellerUsd.toFixed(2)} vs. single store
                    </span>
                  )}
                  {result.collectionSummary.excludedBasicsQty > 0 && (
                    <span className="text-slate-400">
                      {result.collectionSummary.excludedBasicsQty} basics skipped
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                disabled={busy || result.packages.length === 0}
                onClick={() => handleStockReoptimize()}
                className="px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-extrabold text-xs flex items-center gap-1.5 shrink-0"
                title="Verify live vendor stock and immediately re-route any sold-out listing"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                <span>Verify Stock / Re-Optimize OOS</span>
              </button>
            </div>
          )}

          {/* Out-of-Stock Re-Optimization Delta Report */}
          {reoptBanner && reoptBanner.affectedCards.length > 0 && (
            <div className="p-3 rounded-xl bg-amber-950/50 border border-amber-500/50 space-y-1">
              <div className="font-bold text-amber-200 text-xs">
                Live Stock Re-Optimization Complete (
                {reoptBanner.totalDeltaUsd >= 0 ? `+$${reoptBanner.totalDeltaUsd.toFixed(2)}` : `-$${Math.abs(reoptBanner.totalDeltaUsd).toFixed(2)}`}
                )
              </div>
              {reoptBanner.affectedCards.map((ac, i) => (
                <div key={`${ac.cardName}-${i}`} className="text-[11px] text-amber-100/90">
                  • <strong>{ac.cardName}</strong> ({ac.reason.replace(/_/g, ' ')} at {ac.previousSellerName}) →
                  rerouted to <strong>{ac.newSellerNames.join(', ') || 'unfulfilled'}</strong>
                </div>
              ))}
            </div>
          )}

          {/* Vendor Packages & Checkout Checklists */}
          {result && result.packages.length > 0 && (
            <div className="space-y-3">
              {result.packages.map((pkg, pkgIdx) => (
                <div
                  key={pkg.sellerId}
                  className="rounded-xl bg-slate-900 border border-slate-800 overflow-hidden"
                >
                  <div className="px-3.5 py-2.5 bg-slate-900/95 border-b border-slate-800 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 font-extrabold text-[10px]">
                        Package #{pkgIdx + 1}
                      </span>
                      <span className="font-extrabold text-sm text-white">{pkg.sellerName}</span>
                      <span className="text-[11px] text-slate-400">
                        ({pkg.vendorLabel} · {pkg.totalCardQty} cards)
                      </span>
                      {pkg.freeShippingUnlocked && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-slate-950 font-black text-[10px]">
                          FREE SHIPPING
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-sm text-emerald-300">
                        ${pkg.totalUsd.toFixed(2)}{' '}
                        <span className="text-[10px] font-normal text-slate-400">
                          (${pkg.subtotalUsd.toFixed(2)} + ${pkg.shippingUsd.toFixed(2)} ship)
                        </span>
                      </span>

                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(pkg.massEntryText);
                          setCopiedSellerId(pkg.sellerId);
                          setTimeout(() => setCopiedSellerId(null), 1500);
                        }}
                        className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold text-[11px] flex items-center gap-1"
                      >
                        {copiedSellerId === pkg.sellerId ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-400" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy Mass Entry</span>
                          </>
                        )}
                      </button>

                      <a
                        href={pkg.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] flex items-center gap-1"
                      >
                        <span>Open Store</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>

                  {/* Step-by-Step Prefilled Checkout Checklist */}
                  <div className="px-3.5 py-2 bg-slate-950/60 border-b border-slate-800/80 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {pkg.stepChecklist.map((step, sIdx) => {
                      const stepKey = `${pkg.sellerId}:${sIdx}`;
                      const done = Boolean(checkedSteps[stepKey]);
                      return (
                        <label
                          key={stepKey}
                          className={`flex items-center gap-2 text-[11px] cursor-pointer ${
                            done ? 'line-through text-slate-500' : 'text-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={done}
                            onChange={(e) =>
                              setCheckedSteps((prev) => ({ ...prev, [stepKey]: e.target.checked }))
                            }
                            className="accent-emerald-500"
                          />
                          <span>{step}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Compact Allocated Card List */}
                  <div className="max-h-44 overflow-y-auto divide-y divide-slate-800/60 px-3.5">
                    {pkg.items.map((line) => (
                      <div
                        key={line.quoteId}
                        className="py-1.5 flex items-center justify-between gap-2 text-[11px]"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono font-bold text-emerald-300 w-5">{line.quantity}x</span>
                          <span className="font-semibold text-slate-100 truncate">{line.cardName}</span>
                          <span className="px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 text-[10px]">
                            {line.setCode} #{line.collectorNumber}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {line.condition}
                            {line.foil ? ' · Foil' : ''}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-slate-200">${line.lineTotalUsd.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() =>
                              handleStockReoptimize([{ quoteId: line.quoteId, availableQty: 0 }])
                            }
                            title="Mark this seller listing Out-of-Stock and re-route"
                            className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-rose-900/70 text-[10px] text-slate-400 hover:text-rose-200"
                          >
                            OOS
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {result && result.packages.length === 0 && (
            <div className="p-6 rounded-xl bg-slate-900/60 border border-slate-800 text-center text-slate-400 flex flex-col items-center gap-2">
              <PackageCheck className="w-6 h-6 text-emerald-400" />
              <span>All cards in this deck are already covered by your owned collection / skipped basics!</span>
            </div>
          )}

          {/* Target-Total Daily Price-Drop Alerts */}
          <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 font-bold text-amber-300">
                <Bell className="w-3.5 h-3.5" />
                <span>Daily Price-Drop Alert ("Notify Me Below Target")</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-[11px]">Target &lt;= $</span>
                <input
                  type="number"
                  step="0.50"
                  min="1"
                  aria-label="Target cart total in USD"
                  value={targetUsdInput}
                  onChange={(e) => setTargetUsdInput(e.target.value)}
                  className="w-24 px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 font-mono font-bold text-white"
                />
                <button
                  type="button"
                  onClick={handleCreateAlert}
                  className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-[11px]"
                >
                  Save Alert
                </button>
              </div>
            </div>

            {alertFeedback && <div className="text-[11px] text-emerald-300 font-semibold">{alertFeedback}</div>}

            {alerts.length > 0 && (
              <div className="space-y-1 pt-1.5 border-t border-slate-800">
                {alerts.map((al) => (
                  <div
                    key={al.alertId}
                    className="py-1 px-2 rounded-lg bg-slate-950/70 flex items-center justify-between gap-2 text-[11px]"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                          al.triggered
                            ? 'bg-emerald-500/25 text-emerald-300'
                            : 'bg-amber-500/20 text-amber-200'
                        }`}
                      >
                        {al.triggered ? 'HIT' : 'WATCHING'}
                      </span>
                      <span className="font-semibold text-slate-200 truncate">{al.deckName}</span>
                      <span className="text-slate-400">
                        Target &lt;= ${al.targetTotalUsd.toFixed(2)} (last ${al.lastCheckedTotalUsd.toFixed(2)})
                      </span>
                    </div>
                    <button
                      type="button"
                      aria-label={`Delete alert for ${al.deckName}`}
                      onClick={async () => {
                        await playtableApi.deletePriceAlert(al.alertId);
                        setAlerts((prev) => prev.filter((x) => x.alertId !== al.alertId));
                      }}
                      className="p-1 rounded hover:bg-rose-950 text-rose-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
